import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterAbility,
  AbilityEffect,
  CharacterSheetInterface,
  CharacterSpell,
  Attack,
  ExtraEffectsFeature,
  CharacterFeature,
  ExtraEffectsOfTypeFeature,
  CharacterAction,
  CharacterExtraEffects,
  StoredSpell,
  TransformOption2,
} from "@dndModels";
import { DamageType, SpellLevel } from "@dndModels/Types";

import API from "@utilities/smart_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
// import DataUtilities from '../../../utilities/common_utilities';

import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";

import ModalOrDrawer from "@components/display/ModalOrDrawer";
import LoadingBox from "@components/input/LoadingBox";
import CharacterTransformOptions from '@dndComponents/character/misc/CharacterTransformOptions';
import DisplayTransformOption from '@dndComponents/display/DisplayTransformOption';

interface Props {
  char: CharacterSheetInterface;
  spell?: CharacterAbility | CharacterSpell | StoredSpell;
  action: CharacterAction;
  effect: AbilityEffect;
  cee?: CharacterExtraEffects;
  processing: boolean;
  disabled: boolean;
  log_it?: boolean;
  ee_cf?: CharacterFeature;
  type_cf?: CharacterFeature;
  shared: boolean;
  level: SpellLevel;
  wide: boolean;
  show_extra_effects?: boolean;
  attack_mod?: number;
  main_damage_type?: DamageType;
  casting_time: string; // 'A' | 'BA' | 'RA' | 'Special' | 'Attack' | "1 Minute" | "10 Minutes" | "1 Hour";
  onChange: (change_types: string[]) => void; // For slots, resources, and concentration
  hide_touch?: boolean;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
// const common_util = DataUtilities.getInstance();

export default function CharacterTransformEffect({
  char,
  spell,
  action,
  effect,
  cee,
  processing,
  disabled,
  ee_cf,
  shared,
  level,
  attack_mod = 0,
  main_damage_type,
  hide_touch = false,
  onChange,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [is_processing, setProcessing] = useState(false);
  // const [ping, setPing] = useState(false);
  // const [keep_rolls, setKeepRolls] = useState<any[]>([]);
  const [option_list, setOptionList] = useState<TransformOption2[]>([]);
  const [selected_option, setSelectedOption] = useState<TransformOption2 | null>(null);
  // const [selected_id, setSelectedId] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback( async () => {
    // if (ee_cf && ee_cf.name.includes("Blood")) {
      // debugger;
    // }
    const potence = ee_cf && (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) ? 
      char_util.getPotence({ slot_level: level, char, item_id, spell: ee_cf, effect, cfs_id: (ee_cf ? ee_cf.source_id : ""), attack_mod }) : 
      spell ? char_util.getPotence({ slot_level: level, char, item_id, spell, effect, cfs_id: "", attack_mod, action }) : null;
    // let extra_string = "";
    if (potence) {
      // if (spell && spell.name.includes("Familiar")) {
      //   debugger;
      // }
      setLoading(true);
      // extra_string = potence.extra;
      const options = await char_util.getTransformOption2s(char, potence); // , action.name.includes("Pact")
      setOptionList(options);
      setLoading(false);
    }
  }, []);
   
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  useEffect(() => {
    if (is_processing !== processing) {
      setProcessing(processing);
    }
    // eslint-disable-next-line
  }, [processing]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const enabled = shared || (!disabled && api.user && (char.owner_id === api.user._id || (char instanceof Character && char.campaign && char.campaign.owner_id === api.user._id)));
  const item_id = spell instanceof CharacterAbility ? spell.item_id : (spell instanceof CharacterSpell ? spell.source_true_id : "");

  const renderExtras = () => {
    return (
      <>
        <ModalOrDrawer
          open={open}
          onClose={() => { 
            setOpen(false); 
            char.subscription_holder.modalClosed();
          }}
        >
          <>
            { open && renderTransformOption2s() }
          </>
        </ModalOrDrawer>
      </>
    );
  }

  const renderTransformOption2s = () => {
    const option = option_list.length === 1 ? option_list[0] : selected_option;
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12} className="header centered">
          Transform Options
        </Grid>
        <Grid size={12}>
          <CharacterTransformOptions 
            name="Choose"
            character={char}
            value={option}
            option_list={option_list}
            shared={shared}
            index
            onChange={setSelectedOption}
          />
        </Grid>
        { selected_option && char instanceof Character &&
          <>
            { spell ?
              <Grid size={12}>
                <DisplayTransformOption
                  char={char}
                  obj={selected_option}
                  spell={spell}
                  level={level}
                />
              </Grid>
            : ee_cf &&
              <Grid size={12}>
                <DisplayTransformOption
                  char={char}
                  obj={selected_option}
                  spell={ee_cf}
                  level={level}
                />
              </Grid>
            }
          </>
        }
      </Grid>
    );
  }

  // The name of this function is a joke. It's supposed to be the transformers sound. This spelling is apparently the one used for it in wikis.
  const tscheChuChuChuTsche = async (selected_option: TransformOption2) => {
    // debugger;
    if (char instanceof Character) {
      if (spell) {
        setProcessing(true);
        await char_util.createTransform({ char, spell, level, selected_option });
        setProcessing(false);
        onChange(["Minions"]);
      } else if (ee_cf) {
        setProcessing(true);
        await char_util.createTransform({ char, spell: ee_cf, level, selected_option });
        setProcessing(false);
        onChange(["Minions"]);
      }
    }
  }

  const viewSelected = () => {
    // Opens a modal with a viewable version of the statblock without actually summoning it
    setOpen(true);
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    const option = option_list.length === 1 ? option_list[0] : selected_option;
    let range_string = spell instanceof Attack ? "Self" : char_util.getRangeString({ char, spell, effect });
    if (hide_touch && (range_string === "Touch" || range_string === "Self")) {
      range_string = "";
    }
    return (
      <Grid className="centered"
        container spacing={0} direction="row">
        <Grid size={4} className="centered">
          { range_string }
        </Grid>
        <Grid size={4}>
          { option_list.length !== 1 ?
            <CharacterTransformOptions 
              name="Choose"
              character={char}
              // potence={potence}
              value={option}
              option_list={option_list}
              shared={shared}
              onChange={setSelectedOption}
            />
          : option &&
            <>{ option.name }</>
          }
        </Grid>
        <Grid size={4}>
          <Grid container spacing={0} direction="row">
            <Grid size={{ xs: 12, sm: 6 }}>
              <DandDButtonBox 
                name="Transform"
                disabled={ !enabled || option === null || option === undefined || processing }
                onClick={() => {
                  // debugger;
                  if (option) {
                    tscheChuChuChuTsche(option);
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DandDButtonBox 
                name="View"
                disabled={ !enabled }
                onClick={() => {
                  viewSelected();
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        { renderExtras() }
      </Grid>
    );
  }
}
